/**
 * Texto de sucursales/cajas para encabezados en PDF y Excel.
 */

/** Campos de filtros UI que no deben quedar «cacheados» desde el API. */
const ENCABEZADO_FILTROS_UI_KEYS = [
  'sucursalesTexto',
  'sucursalesItems',
  'cajasTexto',
  'cajasItems',
];

/**
 * Prioriza sucursales/cajas del formulario (fallback) sobre lo que devuelva el API.
 */
export function applyEncabezadoFiltrosUiFromFallback(merged, fallback) {
  if (!merged || typeof merged !== 'object') return merged || {};
  if (!fallback || typeof fallback !== 'object') return merged;
  const out = { ...merged };
  for (const k of ENCABEZADO_FILTROS_UI_KEYS) {
    const v = fallback[k];
    if (v == null) continue;
    if (Array.isArray(v)) {
      out[k] = v;
    } else if (String(v).trim()) {
      out[k] = v;
    }
  }
  return out;
}

function pickItems(enc, kind) {
  const e = enc && typeof enc === 'object' ? enc : {};
  const key = kind === 'sucursales' ? 'sucursalesItems' : 'cajasItems';
  const textoKey = kind === 'sucursales' ? 'sucursalesTexto' : 'cajasTexto';
  const items = Array.isArray(e[key])
    ? e[key].map((x) => String(x ?? '').trim()).filter(Boolean)
    : [];
  const texto = String(e[textoKey] ?? '').trim();
  return { items, texto };
}

/**
 * @param {'Sucursales'|'Cajas'} label
 * @param {object} enc
 * @param {{ fallbackText?: string, uppercase?: boolean }} [opts]
 * @returns {string[]}
 */
export function buildFiltroEncabezadoExportLines(label, enc, opts = {}) {
  const kind = label.toLowerCase().includes('sucursal') ? 'sucursales' : 'cajas';
  const { uppercase = false } = opts;
  const fmt = (s) => {
    const t = String(s ?? '').trim();
    return uppercase ? t.toUpperCase() : t;
  };

  const { items, texto } = pickItems(enc, kind);
  const formattedItems = items.map(fmt);
  const formattedTexto = fmt(texto);
  const fallback = fmt(opts.fallbackText || '');

  if (formattedItems.length > 1) {
    return [`${label}:`, ...formattedItems.map((item) => `  • ${item}`)];
  }
  if (formattedItems.length === 1) {
    return [`${label}: ${formattedItems[0]}`];
  }
  if (formattedTexto) {
    return [`${label}: ${formattedTexto}`];
  }
  if (fallback) {
    return [fallback.startsWith(`${label}:`) ? fallback : `${label}: ${fallback}`];
  }
  return [];
}

/** Añade filas de sucursales y cajas a un AOA de Excel. */
export function appendFiltrosSucursalesCajasExcel(aoa, enc, form = {}) {
  const fb = form && typeof form === 'object' ? form : {};
  let cajasFallback = '';
  if (fb.cajaDescripcion) {
    cajasFallback = String(fb.cajaDescripcion).replace(/^Cajas:\s*/i, '').trim();
  } else if (fb.cajaInicio != null || fb.cajaFin != null) {
    cajasFallback = `${fb.cajaInicio ?? '—'} a ${fb.cajaFin ?? '—'}`;
  }

  for (const line of buildFiltroEncabezadoExportLines('Sucursales', enc)) {
    aoa.push([line]);
  }
  for (const line of buildFiltroEncabezadoExportLines('Cajas', enc, { fallbackText: cajasFallback })) {
    aoa.push([line]);
  }
}

/**
 * Dibuja sucursales y cajas centrados en PDF.
 * @returns {number} siguiente Y
 */
export function drawFiltrosSucursalesCajasPdf(doc, enc, pageW, margin, y, form = {}) {
  const fb = form && typeof form === 'object' ? form : {};
  let cajasFallback = '';
  if (fb.cajaDescripcion) {
    cajasFallback = String(fb.cajaDescripcion).replace(/^Cajas:\s*/i, '').trim();
  } else if (fb.cajaInicio != null || fb.cajaFin != null) {
    cajasFallback = `${fb.cajaInicio ?? '—'} a ${fb.cajaFin ?? '—'}`;
  }

  const lines = [
    ...buildFiltroEncabezadoExportLines('Sucursales', enc),
    ...buildFiltroEncabezadoExportLines('Cajas', enc, { fallbackText: cajasFallback }),
  ];

  if (!lines.length) return y;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  for (const line of lines) {
    const wrapped = doc.splitTextToSize(line, pageW - margin * 2);
    for (let i = 0; i < wrapped.length; i += 1) {
      doc.text(wrapped[i], pageW / 2, y, { align: 'center' });
      y += 3.5;
    }
  }
  return y;
}

/** Variante para bloques Excel centrados (una celda por línea). */
export function pushFiltrosSucursalesCajasCentered(pushLine, enc, form = {}) {
  const fb = form && typeof form === 'object' ? form : {};
  let cajasFallback = '';
  if (fb.cajaDescripcion) {
    cajasFallback = String(fb.cajaDescripcion).replace(/^Cajas:\s*/i, '').trim();
  } else if (fb.cajaInicio != null || fb.cajaFin != null) {
    cajasFallback = `${fb.cajaInicio ?? '—'} a ${fb.cajaFin ?? '—'}`;
  }

  for (const line of buildFiltroEncabezadoExportLines('Sucursales', enc)) {
    pushLine(line);
  }
  for (const line of buildFiltroEncabezadoExportLines('Cajas', enc, { fallbackText: cajasFallback })) {
    pushLine(line);
  }
}
